import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Calendar,
  Users,
  MapPin,
  TrendingUp,
  DollarSign,
  Receipt,
  Edit2,
  PlusCircle,
  Briefcase,
  FileText,
} from "lucide-react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { useTranslation } from "react-i18next";
import BossCreateDayWork from "../BossDailyWorkComps/BossCreateDailyWork";
import BossCreateExpenses from "../BossStatisticComps/BossCreateExpenses";

const getExpenseTypeNames = (t) => ({
  1: t("materials"),
  2: t("equipment"),
  3: t("transportation"),
  4: t("utilities"),
  5: t("otherExpense"),
});

export default function BossStatisticByDateData() {
  const { t } = useTranslation();
  const expenseTypeNames = getExpenseTypeNames(t);
  const history = useHistory();

  const [allData, setAllData] = useState(null);
  const [choosedDate, setChoosedDateState] = useState(null);
  const [choosedMonth, setChoosedMonth] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [selectedPlaceId, setSelectedPlaceId] = useState(null);
  const [existingEmployees, setExistingEmployees] = useState([]);

  // Load from localStorage
  useEffect(() => {
    const data = localStorage.getItem("allData");
    const date = localStorage.getItem("choosedDate");
    const month = localStorage.getItem("choosedMonth");
    if (data) setAllData(JSON.parse(data));
    if (date) setChoosedDateState(date);
    if (month) setChoosedMonth(month);
    setLoading(false);
  }, []);

  // Persist chosen date
  const setChoosedDate = (date) => {
    setChoosedDateState(date);
    localStorage.setItem("choosedDate", date);
  };

  if (loading || !allData || !choosedDate) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ── Days in month ─────────────────────────────────────────────────────────
  const getAllDaysInMonth = () => {
    if (!choosedMonth) return [];
    const [month, year] = choosedMonth.split("-");
    const daysInMonth = moment(`${year}-${month}`, "YYYY-MM").daysInMonth();
    const days = [];
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(`${String(day).padStart(2, "0")}-${month}-${year}`);
    }
    return days;
  };
  const allDays = getAllDaysInMonth();

  // ── Filter data for chosen date ───────────────────────────────────────────
  const dateWorkData = (allData.myDailyWork || []).filter(
    (item) => item.date === choosedDate,
  );
  const dateExpenses = (allData.myExpenses || []).filter(
    (expense) => expense.date === choosedDate,
  );

  // ── Group work by place ───────────────────────────────────────────────────
  const groupByPlace = () => {
    const grouped = {};
    dateWorkData.forEach((item) => {
      if (!grouped[item.placeId]) grouped[item.placeId] = [];
      grouped[item.placeId].push(item);
    });
    return grouped;
  };
  const groupedByPlace = groupByPlace();

  // ── Totals ────────────────────────────────────────────────────────────────
  const calculateStats = () => {
    let totalSalaries = 0;
    let totalRevenue = 0;
    dateWorkData.forEach((work) => {
      totalSalaries += parseFloat(work.salary) || 0;
      const place = (allData.myPlaces || []).find((p) => p.id === work.placeId);
      if (place?.paidWay === 2 && work.placeDailySalary) {
        totalRevenue += parseFloat(work.placeDailySalary) || 0;
      }
    });
    return { totalSalaries, totalRevenue };
  };

  const { totalSalaries, totalRevenue } = calculateStats();
  const totalExpenses = dateExpenses.reduce(
    (s, e) => s + (parseFloat(e.price) || 0),
    0,
  );
  const totalEmployees = new Set(dateWorkData.map((w) => w.employeeId)).size;
  const totalPlaces = Object.keys(groupedByPlace).length;

  // ── Helpers ───────────────────────────────────────────────────────────────
  const getPlace = (placeId) =>
    (allData.myPlaces || []).find((p) => p.id === placeId);
  const getPlaceName = (placeId) =>
    getPlace(placeId)?.place || `Place ${placeId}`;
  const getEmployeeName = (employeeId) =>
    (allData.myEmployees || []).find((e) => e.id === employeeId)?.name ||
    `Employee ${employeeId}`;

  // ── Edit place handler ────────────────────────────────────────────────────
  const handleEditPlace = (placeId) => {
    const placeWork = groupedByPlace[placeId] || [];
    const existingEmps = placeWork.map((item) => {
      const emp = (allData.myEmployees || []).find(
        (e) => e.id === item.employeeId,
      );
      return {
        id: item.employeeId,
        name: emp?.name || `Employee ${item.employeeId}`,
        salary: item.salary.toString(),
        placeDailySalary: item.placeDailySalary?.toString() || "",
      };
    });
    setSelectedPlaceId(placeId);
    setExistingEmployees(existingEmps);
    setShowCreateModal(true);
  };

  const handleCreateDailyWork = () => {
    setSelectedPlaceId(null);
    setExistingEmployees([]);
    setShowCreateModal(true);
  };

  // Payment badge helper
  const paymentBadge = (paidWay) => {
    if (paidWay === 1)
      return {
        label: t("monthly"),
        emoji: "📅",
        bg: "bg-blue-50",
        text: "text-blue-600",
      };
    if (paidWay === 2)
      return {
        label: t("daily"),
        emoji: "💰",
        bg: "bg-green-50",
        text: "text-green-600",
      };
    return {
      label: "Global",
      emoji: "🌍",
      bg: "bg-purple-50",
      text: "text-purple-600",
    };
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Header ── */}
      <div className="bg-white px-5 py-4 rounded-b-3xl shadow-md">
        <div className="flex items-center gap-3">
          <button
            onClick={() => history.goBack()}
            className="w-9 h-9 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>

          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center flex-shrink-0">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-gray-900 truncate">
                {moment(choosedDate, "DD-MM-YYYY").format("MMMM DD, YYYY")}
              </h1>
              <p className="text-sm text-gray-500">
                {moment(choosedDate, "DD-MM-YYYY").format("dddd")}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-5 py-5 space-y-6 pb-24">
        {/* ── Day Selector ── */}
        <div>
          <h2 className="text-lg font-bold text-gray-900 mb-3">{t("daily")}</h2>
          <div className="flex gap-3 overflow-x-auto pb-2 hide-scrollbar">
            {allDays.map((date) => {
              const isSelected = date === choosedDate;
              const hasWork = (allData.myDailyWork || []).some(
                (w) => w.date === date,
              );
              const hasExpenses = (allData.myExpenses || []).some(
                (e) => e.date === date,
              );
              const hasActivity = hasWork || hasExpenses;

              return (
                <button
                  key={date}
                  onClick={() => setChoosedDate(date)}
                  className={`relative flex-shrink-0 flex flex-col items-center px-4 py-3 rounded-2xl border-2 min-w-[70px] transition-all ${
                    isSelected
                      ? "bg-yellow-400 border-yellow-400 shadow-md"
                      : hasActivity
                        ? "bg-white border-yellow-200 hover:border-yellow-300"
                        : "bg-white border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <span
                    className={`text-lg font-bold ${isSelected ? "text-white" : "text-gray-900"}`}
                  >
                    {moment(date, "DD-MM-YYYY").format("DD")}
                  </span>
                  <span
                    className={`text-xs font-semibold ${isSelected ? "text-yellow-100" : "text-gray-500"}`}
                  >
                    {moment(date, "DD-MM-YYYY").format("ddd")}
                  </span>
                  {/* Activity indicator dot */}
                  {hasActivity && !isSelected && (
                    <div className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-yellow-400" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Summary Cards ── */}
        <div className="space-y-4">
          {/* Employees + Places row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl p-5 shadow-sm border-l-4 border-blue-500">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                <Users className="w-6 h-6 text-blue-500" />
              </div>
              <p className="text-2xl font-bold text-gray-900 mb-1">
                {totalEmployees}
              </p>
              <p className="text-sm text-gray-500 font-medium">{t("employees")}</p>
            </div>

            <div className="bg-white rounded-2xl p-5 shadow-sm border-l-4 border-yellow-400">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                <MapPin className="w-6 h-6 text-yellow-500" />
              </div>
              <p className="text-2xl font-bold text-gray-900 mb-1">
                {totalPlaces}
              </p>
              <p className="text-sm text-gray-500 font-medium">{t("place")}</p>
            </div>
          </div>

          {/* Revenue — only if daily payment places have revenue */}
          {totalRevenue > 0 && (
            <div className="bg-white rounded-2xl p-5 shadow-sm border-l-4 border-green-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium mb-2">
                    {t("totalRevenue")}
                  </p>
                  <p className="text-3xl font-bold text-green-500">
                    ₪{totalRevenue.toLocaleString()}
                  </p>
                </div>
                <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-8 h-8 text-green-500" />
                </div>
              </div>
            </div>
          )}

          {/* Total Salaries */}
          <div className="bg-white rounded-2xl p-5 shadow-sm border-l-4 border-red-400">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-2">
                  {t("totalSalaries")}
                </p>
                <p className="text-3xl font-bold text-red-400">
                  ₪{totalSalaries.toLocaleString()}
                </p>
              </div>
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                <DollarSign className="w-8 h-8 text-red-400" />
              </div>
            </div>
          </div>

          {/* Total Expenses */}
          <div className="bg-white rounded-2xl p-5 shadow-sm border-l-4 border-yellow-400">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-2">
                  {t("totalExpenses")}
                </p>
                <p className="text-3xl font-bold text-yellow-500">
                  ₪{totalExpenses.toLocaleString()}
                </p>
              </div>
              <div className="w-16 h-16 bg-yellow-50 rounded-full flex items-center justify-center">
                <Receipt className="w-8 h-8 text-yellow-500" />
              </div>
            </div>
          </div>
        </div>

        {/* ── Expenses Section ── */}
        {dateExpenses.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-gray-900">{t("expensesAndInvoices")}</h2>
              <button
                onClick={() => setShowExpenseModal(true)}
                className="w-9 h-9 bg-yellow-400 hover:bg-yellow-500 rounded-full flex items-center justify-center transition shadow-md"
              >
                <PlusCircle className="w-5 h-5 text-white" />
              </button>
            </div>

            <div className="space-y-3">
              {dateExpenses.map((expense) => (
                <div
                  key={expense.id}
                  className="bg-white rounded-2xl p-4 border-2 border-yellow-100 shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Receipt className="w-5 h-5 text-yellow-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-base font-bold text-gray-900 truncate">
                        {expense.title ||
                          expenseTypeNames[expense.type] ||
                          "Expense"}
                      </p>
                      <p className="text-xs text-gray-400">
                        {expenseTypeNames[expense.type] || "Other"}
                        {expense.placeId &&
                          ` • ${getPlaceName(expense.placeId)}`}
                      </p>
                    </div>
                    <span className="bg-yellow-100 text-yellow-600 font-bold text-sm px-3 py-1.5 rounded-xl flex-shrink-0">
                      ₪{parseFloat(expense.price).toLocaleString()}
                    </span>
                  </div>

                  {expense.note && expense.note.trim() !== "" && (
                    <div className="flex items-start gap-2 mt-3 pt-3 border-t border-gray-100">
                      <FileText className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-gray-500 leading-relaxed">
                        {expense.note}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Work Details Section ── */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-gray-900">{t("dailyOperations")}</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={handleCreateDailyWork}
                className="w-9 h-9 bg-blue-500 hover:bg-blue-600 rounded-full flex items-center justify-center transition shadow-md"
                title="Add Daily Work"
              >
                <Briefcase className="w-4 h-4 text-white" />
              </button>
              {dateExpenses.length === 0 && (
                <button
                  onClick={() => setShowExpenseModal(true)}
                  className="w-9 h-9 bg-yellow-400 hover:bg-yellow-500 rounded-full flex items-center justify-center transition shadow-md"
                  title="Add Expense"
                >
                  <PlusCircle className="w-5 h-5 text-white" />
                </button>
              )}
            </div>
          </div>

          {dateWorkData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-5">
                <Briefcase className="w-12 h-12 text-gray-300" />
              </div>
              <p className="text-lg font-semibold text-gray-500 mb-2 text-center">
                {t("noActivity")}
              </p>
              <p className="text-sm text-gray-400 text-center mb-6">
                {t("noDataThisMonth")}
              </p>
              <button
                onClick={handleCreateDailyWork}
                className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-semibold px-6 py-3 rounded-xl transition shadow-md"
              >
                <PlusCircle className="w-5 h-5" />
                {t("dailyOperations")}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.keys(groupedByPlace).map((placeId) => {
                const placeWork = groupedByPlace[placeId];
                const place = getPlace(parseInt(placeId));
                const badge = paymentBadge(place?.paidWay);

                const placeTotalSalary = placeWork.reduce(
                  (s, w) => s + (parseFloat(w.salary) || 0),
                  0,
                );
                const placeTotalRevenue =
                  place?.paidWay === 2
                    ? placeWork.reduce(
                        (s, w) => s + (parseFloat(w.placeDailySalary) || 0),
                        0,
                      )
                    : 0;

                return (
                  <div
                    key={placeId}
                    className="bg-white rounded-2xl p-4 border-2 border-yellow-100 shadow-sm space-y-3"
                  >
                    {/* Place header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-5 h-5 text-yellow-500" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-0.5">
                            <p className="text-base font-bold text-gray-900 truncate">
                              {getPlaceName(parseInt(placeId))}
                            </p>
                            {place?.paidWay && (
                              <span
                                className={`text-[10px] font-semibold px-2 py-1 rounded-lg ${badge.bg} ${badge.text}`}
                              >
                                {badge.emoji} {badge.label}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-400">
                            {placeWork.length} {t("employees")}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleEditPlace(parseInt(placeId))}
                        className="w-9 h-9 bg-blue-50 hover:bg-blue-100 rounded-full flex items-center justify-center transition flex-shrink-0"
                      >
                        <Edit2 className="w-4 h-4 text-blue-500" />
                      </button>
                    </div>

                    {/* Revenue row — daily payment only */}
                    {place?.paidWay === 2 && placeTotalRevenue > 0 && (
                      <div className="flex items-center justify-between bg-green-50 rounded-xl px-4 py-3">
                        <span className="text-sm font-semibold text-green-800">
                          {t("totalRevenue")}:
                        </span>
                        <span className="text-lg font-bold text-green-500">
                          ₪{placeTotalRevenue.toLocaleString()}
                        </span>
                      </div>
                    )}

                    {/* Salaries total */}
                    <div className="flex items-center justify-between bg-red-50 rounded-xl px-4 py-3">
                      <span className="text-sm font-semibold text-red-800">
                        {t("totalSalaries")}:
                      </span>
                      <span className="text-lg font-bold text-red-400">
                        ₪{placeTotalSalary.toLocaleString()}
                      </span>
                    </div>

                    {/* Employee rows */}
                    <div className="space-y-2">
                      {placeWork.map((work) => (
                        <div
                          key={work.id}
                          className="flex items-center justify-between bg-gray-50 rounded-xl px-3 py-3"
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-9 h-9 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
                              <Users className="w-4 h-4 text-blue-500" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-900 truncate">
                                {getEmployeeName(work.employeeId)}
                              </p>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-[10px] text-gray-400">
                                  ID: {work.employeeId}
                                </span>
                                {place?.paidWay === 2 &&
                                  work.placeDailySalary && (
                                    <>
                                      <span className="text-gray-300 text-[10px]">
                                        •
                                      </span>
                                      <span className="text-[10px] font-semibold text-green-500">
                                        Rev: ₪{work.placeDailySalary}
                                      </span>
                                    </>
                                  )}
                              </div>
                            </div>
                          </div>
                          <span className="bg-red-100 text-red-500 font-bold text-sm px-3 py-1.5 rounded-xl flex-shrink-0 ml-2">
                            ₪{work.salary}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Modals ── */}
      <BossCreateDayWork
        visible={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setExistingEmployees([]);
          setSelectedPlaceId(null);
        }}
        onSuccess={() => {
          setShowCreateModal(false);
          setExistingEmployees([]);
          setSelectedPlaceId(null);
          // Refresh allData from localStorage
          const data = localStorage.getItem("allData");
          if (data) setAllData(JSON.parse(data));
        }}
        places={allData.myPlaces}
        employees={allData.myEmployees}
        preselectedDate={choosedDate}
        preselectedPlaceId={selectedPlaceId}
        preselectedEmployees={existingEmployees}
      />

      <BossCreateExpenses
        visible={showExpenseModal}
        onClose={() => setShowExpenseModal(false)}
        onSuccess={() => {
          setShowExpenseModal(false);
          const data = localStorage.getItem("allData");
          if (data) setAllData(JSON.parse(data));
        }}
        preselectedDate={choosedDate}
      />

      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .animate-spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}
